import { Link as LinkIcon, Building2, Target, Video, Bot, Rocket, Check, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const PLATFORMS = [
  { name: "Meta", color: "#1877F2" },
  { name: "TikTok", color: "#FE2C55" },
  { name: "Google", color: "#4285F4" },
  { name: "YouTube", color: "#FF0000" },
] as const;

const GOAL_MATRIX: { goal: string; support: [boolean, boolean, boolean, boolean] }[] = [
  { goal: "Lead Forms", support: [true, true, true, false] },
  { goal: "WhatsApp Messages", support: [true, false, false, false] },
  { goal: "Sales", support: [true, true, true, true] },
  { goal: "Phone Calls", support: [true, false, true, false] },
  { goal: "Website Traffic", support: [true, true, true, true] },
  { goal: "Views", support: [true, true, false, true] },
  { goal: "Grow Followers", support: [true, true, false, false] },
  { goal: "Promote an Event", support: [true, true, true, false] },
];

function GoalsMatrix() {
  return (
    <div className="mt-5 rounded-2xl border border-border bg-card/60 overflow-hidden">
      <div className="grid grid-cols-[1.4fr_repeat(4,1fr)] items-center px-5 py-3 border-b border-border bg-background/40 text-xs uppercase tracking-wider text-muted-foreground">
        <div>Goal</div>
        {PLATFORMS.map((p) => (
          <div key={p.name} className="flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="hidden sm:inline">{p.name}</span>
          </div>
        ))}
      </div>
      {GOAL_MATRIX.map((row, idx) => (
        <div
          key={row.goal}
          className={`grid grid-cols-[1.4fr_repeat(4,1fr)] items-center px-5 py-3 ${
            idx !== GOAL_MATRIX.length - 1 ? "border-b border-border/60" : ""
          } hover:bg-background/40 transition-colors`}
        >
          <div className="font-semibold text-foreground text-sm">{row.goal}</div>
          {row.support.map((ok, i) => (
            <div key={i} className="flex justify-center">
              {ok ? (
                <span className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />
                </span>
              ) : (
                <span className="w-7 h-7 rounded-full bg-muted/30 flex items-center justify-center">
                  <X className="w-4 h-4 text-muted-foreground/40" />
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function HowItWorks() {
  const { t } = useI18n();
  const steps = [
    { n: "01", icon: LinkIcon, titleKey: "how.s1.title", descKey: "how.s1.desc" },
    { n: "02", icon: Building2, titleKey: "how.s2.title", descKey: "how.s2.desc" },
    { n: "03", icon: Target, titleKey: "how.s3.title", descKey: "how.s3.desc" },
    {
      n: "04",
      icon: Video,
      titleKey: "how.s4.title",
      descKey: "how.s4.desc",
      options: [
        { labelKey: "how.s4.optionA", textKey: "how.s4.optionAText" },
        { labelKey: "how.s4.optionB", textKey: "how.s4.optionBText" },
      ],
    },
    { n: "05", icon: Bot, titleKey: "how.s5.title", descKey: "how.s5.desc" },
    { n: "06", icon: Rocket, titleKey: "how.s6.title", descKey: "how.s6.desc" },
  ];

  return (
    <section id="how-it-works" className="py-24 px-6 relative scroll-mt-20">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            {t("how.title.1")} <span className="text-gradient-orange">{t("how.title.2")}</span> <br />
            {t("how.title.3")} <span className="text-foreground">{t("how.title.4")}</span>
          </h2>
        </div>

        <div className="relative">
          <div className="absolute left-8 top-8 bottom-8 w-px bg-border hidden md:block" />
          <div className="space-y-8">
            {steps.map(({ n, icon: Icon, titleKey, descKey, options }) => (
              <div key={n} className="relative flex gap-6 items-start group">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-card border-2 border-primary/30 flex items-center justify-center relative z-10 group-hover:border-primary transition-colors">
                  <Icon className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1 pt-2">
                  <div className="text-sm font-semibold text-primary mb-1">{t("how.step")} {parseInt(n, 10)}</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-2">{t(titleKey)}</h3>
                  {n === "03" ? (
                    <GoalsMatrix />
                  ) : (
                    <p className="text-muted-foreground leading-relaxed">{t(descKey)}</p>
                  )}
                  {options && (
                    <ul className="mt-4 space-y-2">
                      {options.map((o) => (
                        <li key={o.labelKey} className="text-muted-foreground leading-relaxed">
                          <span className="font-semibold text-foreground">{t(o.labelKey)}:</span> {t(o.textKey)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
